import React, { useCallback, useContext, useMemo, useState } from 'react';
import DealRoomFieldsRequiredVisibleInfoContext from './DealRoomFieldsRequiredVisibleInfo.context';
import { DealRoom, DealRoomRequiredVisibleInfo } from 'models/DealRoom';
import { REASON_TO_HIDE, RequiredVisibleInfo } from 'models/Listing';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

const prepareDealRoomRequiredVisibleInfo = (values: RequiredVisibleInfo) => {
  const {
    isRequired,
    isVisible,
    reasonToHide,
    customReasonToHide,
    revealAfter,
  } = values || {};

  return {
    isRequired,
    isVisible,
    reasonToHide: isVisible ? null : reasonToHide,
    customReasonToHide: isVisible
      ? null
      : reasonToHide === REASON_TO_HIDE.CUSTOM
      ? customReasonToHide
      : null,
    revealAfter: isVisible ? null : revealAfter,
  };
};

type DealRoomFieldsRequiredVisibleInfoProps = {
  dealRoomId: string;
  dealRoom?: Partial<DealRoom>;
  canChangeRequired: boolean;
};

const DealRoomFieldsRequiredVisibleInfo: React.FC<
  DealRoomFieldsRequiredVisibleInfoProps
> = (props) => {
  const { children, dealRoomId, dealRoom, canChangeRequired } = props;

  const {
    amountFromRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    amountRequiredVisibleInfo,
    averageStockValueRequiredVisibleInfo,
  } = dealRoom || {};

  const initialValue = useMemo(
    () => ({
      amountFromRequiredVisibleInfo,
      amountToRequiredVisibleInfo,
      amountRequiredVisibleInfo,
      averageStockValueRequiredVisibleInfo,
    }),
    [
      amountFromRequiredVisibleInfo,
      amountToRequiredVisibleInfo,
      amountRequiredVisibleInfo,
      averageStockValueRequiredVisibleInfo,
    ],
  );

  const [fields, setFields] =
    useState<Partial<DealRoomRequiredVisibleInfo>>(initialValue);

  const { popServerError } = useContext(PopNotificationsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);

  const updateField = useCallback(
    async (
      fieldName: keyof DealRoomRequiredVisibleInfo,
      values: RequiredVisibleInfo,
      onSuccess: () => void,
    ) => {
      const data = prepareDealRoomRequiredVisibleInfo(values);

      try {
        api.dealRoom.updateDealRoom(
          dealRoomId,
          { [fieldName]: data },
          isSuperAdmin,
        );
        setFields((prev) => ({ ...prev, [fieldName]: data }));
        onSuccess();
      } catch (e) {
        popServerError(e);
      }
    },
    [dealRoomId, isSuperAdmin, popServerError],
  );

  return (
    <DealRoomFieldsRequiredVisibleInfoContext.Provider
      value={{ fields, canChangeRequired, updateField }}
    >
      {children}
    </DealRoomFieldsRequiredVisibleInfoContext.Provider>
  );
};

export default DealRoomFieldsRequiredVisibleInfo;
